import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { getCreditPackages, purchaseCredits } from '../api';
import { useNavigate, useLocation } from 'react-router-dom';

function CreditPurchase() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshCredits } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchParams = new URLSearchParams(location.search);
  const source = searchParams.get('source');

  const purchaseSectionRef = useRef(null);

  // ✅ Fetch credit packages from API on component mount
  useEffect(() => {
    const fetchCreditPackages = async () => {
      try {
        setIsLoading(true);
        setError(null);
        console.log('📦 Fetching credit packages from backend API...');

        const response = await getCreditPackages();

        if (response.data && response.data.success) {
          const packages = response.data.packages.map(pkg => ({
            id: pkg.packageId,
            credits: pkg.baseCredits,
            price: pkg.price,
            badge: pkg.packageName,
            badgeColor: pkg.badgeColor,
            bonus: pkg.bonusCredits,
            totalCredits: pkg.totalCredits,
            description: pkg.description,
            features: pkg.features
          }));

          setCreditPackages(packages);
          console.log('✅ Credit packages loaded:', packages);
        } else {
          throw new Error('Failed to fetch credit packages');
        }
      } catch (error) {
        console.error(' Error fetching credit packages:', error);
        setError('Failed to load credit packages. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCreditPackages();
  }, []);

  // ✅ Handle package selection
  const handlePackageSelect = (packageData) => {
    setSelectedPackage(packageData);
    console.log('📦 Package selected:', packageData);

    setTimeout(() => {
      if (purchaseSectionRef.current) {
        purchaseSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // ✅ Generate unique transaction ID
  const generateTransactionId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `TXN${timestamp}_${user.userId}_${selectedPackage.id}_${random}`;
  };

  // ✅ NEW: PayU Purchase Handler
  const handlePurchase = async () => {
    if (!selectedPackage || isProcessing) {
      if (!selectedPackage) {
        alert('⚠️ Please select a credit package first!');
      }
      return;
    }

    if (!user?.userId) {
      alert(' User not found. Please log in again.');
      navigate('/login');
      return;
    }

    setIsProcessing(true);
    setError(null);
    console.log('💳 Processing purchase with PayU:', selectedPackage);

    try {
      // Generate unique transaction ID
      const txnid = generateTransactionId();
      
      // Normalize phone number
      let phoneNumber = user?.phone || user?.mobile || user?.phoneNumber || user?.contact || '';
      phoneNumber = String(phoneNumber || '').replace(/\D/g, '');
      if (phoneNumber.length === 10) phoneNumber = '91' + phoneNumber;

      console.log('📱 Phone used for PayU payment:', phoneNumber);

      // 1) Create purchase record in PENDING state
      const createPurchaseResp = await purchaseCredits(
        user.userId, 
        selectedPackage.id, 
        txnid, 
        'credit_pack',
        'web',
        'PAYU',
        'online',
        'INR',
        'pending' // ✅ IMPORTANT: Create in pending state
      );

      const createdPurchaseId = createPurchaseResp?.data?.purchase?.purchaseId || null;

      if (!createdPurchaseId) {
        throw new Error('Failed to create purchase record before payment');
      }

      console.log('🔖 Created purchase record, purchaseId:', createdPurchaseId);

      // 2) Prepare PayU payment data
      const paymentData = {
        txnid,
        amount: selectedPackage.price,
        productinfo: `${selectedPackage.badge} - ${selectedPackage.totalCredits} Credits`,
        firstname: user?.name || user?.full_name || 'Customer',
        email: user?.email || '',
        phone: phoneNumber,
        udf1: user?.userId, // Store userId for reference
        udf2: createdPurchaseId // Pass purchase_id for callback linking
      };

      // 3) Call backend to get payment hash
      const backendUrl = process.env.REACT_APP_BACKEND || 'http://localhost:4000';
      const response = await fetch(`${backendUrl}/astro/payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        throw new Error('Failed to initialize payment');
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Payment initialization failed');
      }

      // 4) Create PayU form and submit
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://test.payu.in/_payment'; // Test Environment
      // form.action = 'https://secure.payu.in/_payment'; // ✅ Use this for PRODUCTION

      // Add all required PayU fields
      const formFields = {
        key: data.key,
        txnid: data.txnid,
        amount: data.amount,
        productinfo: data.productinfo,
        firstname: data.firstname,
        email: data.email,
        phone: data.phone || paymentData.phone,
        surl: data.surl || `${backendUrl}/astro/payment/success`,
        furl: data.furl || `${backendUrl}/astro/payment/failure`,
        hash: data.hash,
        udf1: data.udf1 || paymentData.udf1,
        udf2: data.udf2 || paymentData.udf2
      };

      // Create hidden inputs
      Object.entries(formFields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value || '';
        form.appendChild(input);
      });

      // Store transaction info in localStorage
      localStorage.setItem('payuTransaction', JSON.stringify({
        txnid: data.txnid,
        amount: data.amount,
        packageId: selectedPackage.id,
        purchaseId: createdPurchaseId,
        credits: selectedPackage.totalCredits
      }));

      // Add form to body and submit
      document.body.appendChild(form);
      console.log('✅ Redirecting to PayU payment gateway...');
      form.submit();

    } catch (error) {
      console.error(' Payment error:', error);

      // Handle duplicate transaction
      if (error.response?.status === 409) {
        alert('⚠️ This transaction has already been processed.');
      } 
      // Handle credit limit error with WhatsApp integration
      else if (error.response?.status === 400 && error.response?.data?.error === 'Credit limit exceeded') {
        const whatsappMessage = encodeURIComponent(
          `${user.full_name || user.name} (${user.email})\n` +
          `Limit extension request\n\n` +
          `Please approve. Thanks!`
        );

        const userResponse = window.confirm(
          `⚠️ Your credit limit has been exceeded!\n\n` +
          `Please contact support for limit extension:\n` +
          `📞 Hemant Rathore\n` +
          `Mobile: +91 8866822366\n\n` +
          `Would you like to contact support via WhatsApp?`
        );

        if (userResponse) {
          window.open(`https://wa.me/918866822366?text=${whatsappMessage}`, '_blank');
        }
      } 
      else {
        const errorMessage = error.response?.data?.error || error.message || 'Payment initialization failed';
        alert(` ${errorMessage}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // ✅ Background style
  const chatContainerStyle = {
    backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url(/uploads/chat.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  return (
    <div className="credit-purchase-container" style={chatContainerStyle}>
      {/* Main Content */}
      <div className="credit-content">
        {/* Title Section */}
        <div className="credit-title-section">
          <h1 className="heading">💎 Get More Cosmic Credits</h1>
          <p className="refer-subtitle">
            Unlock unlimited cosmic wisdom with our credit packages.<br />
            Each question costs 10 credits for Quick Answers and 15 credits for Detailed Answers.
          </p>

          {/* Current Balance Display */}
          <div className="current-balance-display">
            <span className="balance-label">Your Current Balance:</span>
            <span className='dimond'>💎</span>
            <span className="balance-amount"> {user?.credits || 0} Credits</span>
            {user?.credits < 10 && <span className="low-credits-warning">⚠️ Low Credits</span>}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>⚠️</span>
            <span>{error}</span>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="loading-message">
            <span>⏳ Loading credit packages...</span>
          </div>
        )}

        {/* Credit Packages Grid */}
        {!isLoading && (
          <div className="credit-packages-grid">
            {creditPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`credit-package ${pkg.badgeColor} ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                onClick={() => handlePackageSelect(pkg)}
              >
                {/* Badge */}
                <div className={`package-badge badge-${pkg.badgeColor}`}>
                  {pkg.badge}
                </div>

                <div className="package-content">
                  {/* Selection Indicator */}
                  {selectedPackage?.id === pkg.id && (
                    <div className="selection-indicator">
                      <span>✓</span>
                    </div>
                  )}

                  {/* Credits Display */}
                  <div className="package-credits">{pkg.credits}</div>
                  <div className="credits-label">
                    <span className='dimond'>💎</span>
                    <span>Credits</span>
                  </div>

                  {/* Bonus Credits */}
                  {pkg.bonus > 0 && (
                    <div className="bonus-credits">
                      🎁 +{pkg.bonus} Bonus Credits
                    </div>
                  )}

                  {/* Price */}
                  <div className="package-price">₹{pkg.price}</div>

                  {/* Description */}
                  <div className="package-description">{pkg.description}</div>

                  {/* Value Calculation */}
                  <div className="package-value">
                    Total: {pkg.totalCredits} credits •
                    ₹{(pkg.price / pkg.totalCredits).toFixed(1)} per credit
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Purchase Section */}
        <div className="purchase-section" ref={purchaseSectionRef}>
          <button
            className={`main-purchase-btn ${selectedPackage ? 'enabled' : 'disabled'}`}
            onClick={handlePurchase}
            disabled={!selectedPackage || isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="btn-icon">⏳</span>
                <span>Processing Payment...</span>
              </>
            ) : selectedPackage ? (
              <>
                <span className="btn-icon">🛒</span>
                <span>Purchase {selectedPackage.badge} - ₹{selectedPackage.price}</span>
              </>
            ) : (
              <>
                <span className="btn-icon">📦</span>
                <span>Select a Package to Continue</span>
              </>
            )}
          </button>

          {/* Security Info */}
          <div className="step-content">
            <p>🔒 Secure payment powered by PayU • 256-bit SSL encryption</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreditPurchase;
